import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { LookupsApi, PermissoesCredencialApi } from '@/services/api-routes'
import type { LookupItem } from '@/types/lookup'
import { lookupsKeys } from '@/http/queryKeys'
import { defaultOptions } from '@/http/default-query-options'

const opcoesDeLookup = {
  ...defaultOptions,
  placeholderData: keepPreviousData,
} as const

export const useClientesLookupQuery = (termo: string, enabled = true) =>
  useQuery({
    queryKey: lookupsKeys.clientes(termo),
    queryFn: async ({ signal }) => (await LookupsApi.clientes(termo, signal)) ?? [],
    enabled,
    ...opcoesDeLookup,
  })

export const useOperadorasLookupQuery = (termo: string, enabled = true) =>
  useQuery({
    queryKey: lookupsKeys.operadoras(termo),
    queryFn: async ({ signal }) => (await LookupsApi.operadoras(termo, signal)) ?? [],
    enabled,
    ...opcoesDeLookup,
  })

export const useCredenciaisLookupQuery = (
  termo: string,
  clienteId: string | null,
  enabled = true,
) =>
  useQuery<LookupItem[]>({
    queryKey: lookupsKeys.credenciais(termo, clienteId),
    queryFn: async ({ signal }) => (await LookupsApi.credenciais(termo, clienteId, signal)) ?? [],
    enabled,
    ...opcoesDeLookup,
  })

export const useEspecialidadesLookupQuery = (termo: string, enabled = true) =>
  useQuery({
    queryKey: lookupsKeys.especialidades(termo),
    queryFn: async ({ signal }) => (await LookupsApi.especialidades(termo, signal)) ?? [],
    enabled,
    ...opcoesDeLookup,
  })

export const useConselhosQuery = () =>
  useQuery({
    queryKey: lookupsKeys.conselhos(),
    queryFn: async ({ signal }) => (await LookupsApi.conselhos(signal)) ?? [],
    ...defaultOptions,
  })

export const useProfissionaisLookupQuery = (clienteId: string, termo: string, enabled = true) =>
  useQuery({
    queryKey: lookupsKeys.profissionais(clienteId, termo),
    queryFn: async ({ signal }) => (await LookupsApi.profissionais(clienteId, termo, signal)) ?? [],
    enabled,
    ...opcoesDeLookup,
  })

export const useTiposTokenLookupQuery = (enabled = true) =>
  useQuery({
    queryKey: lookupsKeys.tiposToken(),
    queryFn: async ({ signal }) => (await LookupsApi.tiposToken(signal)) ?? [],
    enabled,
    ...defaultOptions,
  })

export const useTiposCredenciaisLookupQuery = (operadoraId: string | null, enabled = true) =>
  useQuery({
    queryKey: lookupsKeys.tiposCredenciais(operadoraId),
    queryFn: async ({ signal }) => (await LookupsApi.tiposCredenciais(operadoraId, signal)) ?? [],
    enabled,
    ...opcoesDeLookup,
  })

export const useTiposArquivosLookupQuery = (enabled = true) =>
  useQuery({
    queryKey: lookupsKeys.tiposArquivos(),
    queryFn: async ({ signal }) => (await LookupsApi.tiposArquivos(signal)) ?? [],
    enabled,
    ...defaultOptions,
  })

export const useUsuariosDisponiveisLookupQuery = (
  credencialId: string,
  termo: string,
  enabled = true,
) =>
  useQuery({
    queryKey: lookupsKeys.usuariosDisponiveis(credencialId, termo),
    queryFn: async ({ signal }) =>
      (await PermissoesCredencialApi.usuariosDisponiveis(credencialId, termo, signal)) ?? [],
    enabled,
    ...opcoesDeLookup,
  })

export const useUnidadesDisponiveisLookupQuery = (
  credencialId: string,
  termo: string,
  enabled = true,
) =>
  useQuery({
    queryKey: lookupsKeys.unidadesDisponiveis(credencialId, termo),
    queryFn: async ({ signal }) =>
      (await PermissoesCredencialApi.unidadesDisponiveis(credencialId, termo, signal)) ?? [],
    enabled,
    ...opcoesDeLookup,
  })
